import { Icon } from "@app/icons";
import { ActionIcon, Button, Checkbox, Collapse, Pagination, Popover } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { EditsBar } from "@ui/EditsBar";
import { cellText, formatNumber } from "@ui/format";
import { editClass, editCounts, editNote } from "@ui/recentEdits";
import { CONTROLS } from "@ui/rowToggle";
import { sortedBy } from "@ui/sort";
import {
  createContext,
  Fragment,
  type HTMLAttributes,
  type MouseEvent,
  memo,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { resizeHandle } from "../resizeHandle";
import { FileButtons } from "./FileButtons";
import { FilterBox } from "./FilterBox";
import { dataTableView } from "./files";
import { FilterRowButton, ResetTableButton, ShownNote, SORT_MARK, SortNote } from "./TableState";
import { COLUMN_WIDTH, type Column, columnName, type DataTableModel, isFilterable, isSortable } from "./useDataTable";

export { type Column, type DataTableModel, useDataTable } from "./useDataTable";

type RowAttributes = HTMLAttributes<HTMLTableRowElement> & { [data: `data-${string}`]: string | number | boolean | undefined };

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(" ") || undefined;

/** Rows a page shows when a table has more. */
const PAGE_SIZE = 50;
// a column's dragged width, set on the table so a drag needs no drawing: the headings and cells read it
const widthVar = (i: number) => `--dtw-${i}`;

type Details = { isOpen: (row: unknown) => boolean; toggle: (row: unknown) => void };
const DetailsContext = createContext<Details | null>(null);

/** The worded button that opens (and closes) a row's details in a ``DataTable`` with ``details``; a click anywhere
 *  else on the row does the same. ``children`` says what it does, open or not ("Show 2 items" / "Hide items"). */
export function DetailsButton({ row, children }: { row: unknown; children: (open: boolean) => ReactNode }) {
  const details = useContext(DetailsContext);
  const open = !!details?.isOpen(row);
  return (
    <Button size="compact-sm" variant="default" aria-expanded={open} onClick={() => details?.toggle(row)}>
      {children(open)}
    </Button>
  );
}

/** A table of ``useDataTable``: its headings sort (click; Shift+click adds a key) and move (drag), a line above it
 *  filters, orders and hides the columns and says what is in force. Inside a flex column it fills the height and
 *  scrolls (headings stay in view); ``className`` goes on that box (``grow``). ``rowProps``: a row's attributes (its
 *  class, click handler…). ``before``: rows shown before a row (group headings) while the rows are in the page's
 *  order, unfiltered. ``empty``: shown when the page gave no rows. Only the rows whose data, attributes or columns
 *  changed are drawn again. Columns with ``edit`` colour the cells typed lately, and a bar above the table explains
 *  them and puts the focused one back (``rowName`` names its row there; ``putBackHelp``: what the put-back does when
 *  it is not saved at once). The Excel and Print buttons take the rows as they are shown (every page) to an .xlsx file
 *  or A4 pages, named after the page, ``title`` (else the ``aria-label``, else the dialog's title) and the day, with
 *  ``about`` on top: what the table belongs to, the page's top part in words ("Sender: …", "Date: …"). */
export function DataTable<T>(props: {
  table: DataTableModel<T>;
  rowKey: (row: T) => string | number;
  rowProps?: (row: T, index: number) => RowAttributes;
  before?: (row: T) => ReactNode;
  /** A row's details, drawn under it when opened (a click on the row, or its ``DetailsButton``); null: it has none. */
  details?: (row: T) => ReactNode;
  /** The row (its place in ``table.rows``) kept in view, its page shown: a cursor the keyboard moves. */
  follow?: number;
  empty?: ReactNode;
  className?: string;
  tableClassName?: string;
  rowName?: (row: T) => string;
  putBackHelp?: string;
  /** What the table is, in a few words: the Excel file's and sheet's name. */
  title?: string;
  /** What the table belongs to (the page's top part, "Sender: …"): lines on top of its Excel file and printout. */
  about?: string[];
  "aria-label"?: string;
}) {
  const { table } = props;
  const { t } = useTranslation();
  const width = table.columns.length;
  const { ownOrder } = table;
  const footed = table.columns.some((c) => c.footer !== undefined);
  const { details, rowKey, follow } = props;
  // opened rows (true) and those sliding shut (false): their details are drawn
  const [opened, setOpened] = useState<ReadonlyMap<string | number, boolean>>(new Map());
  const reduceMotion = useReducedMotion();
  const toggle = useCallback(
    (key: string | number) => {
      setOpened((now) => {
        const next = new Map(now);
        if (!now.get(key)) next.set(key, true);
        else if (reduceMotion) next.delete(key);
        else next.set(key, false);
        return next;
      });
    },
    [reduceMotion],
  );
  const closed = useCallback((key: string | number) => {
    setOpened((now) => {
      if (now.get(key) !== false) return now;
      const next = new Map(now);
      next.delete(key);
      return next;
    });
  }, []);
  const detailsValue = useMemo<Details>(
    () => ({ isOpen: (row) => !!opened.get(rowKey(row as T)), toggle: (row) => toggle(rowKey(row as T)) }),
    [opened, rowKey, toggle],
  );
  const attrsOf = (row: T, index: number): RowAttributes | undefined => {
    const own = props.rowProps?.(row, index);
    if (!details) return own;
    const key = rowKey(row);
    return {
      ...own,
      className: cx(own?.className, "click", opened.get(key) && "dt-open"),
      onClick: (e: MouseEvent<HTMLTableRowElement>) => {
        own?.onClick?.(e);
        if (!(e.target as Element).closest(CONTROLS)) toggle(key);
      },
    };
  };
  // a row's handlers are read when they fire, so a row need not be drawn again for a new closure
  const attrsRef = useRef(attrsOf);
  attrsRef.current = attrsOf;
  const fire = useCallback((row: unknown, index: number, name: string, event: SyntheticEvent) => {
    const handler = (attrsRef.current(row as T, index) as Record<string, unknown> | undefined)?.[name];
    if (typeof handler === "function") handler(event);
  }, []);
  // the typed cell last focused, for the bar above the table
  const [focus, setFocus] = useState<{ row: T; column: string } | null>(null);
  const focusCell = useCallback((row: unknown, column: string) => setFocus({ row: row as T, column }), []);
  const typed = useMemo(() => table.columns.filter((c) => c.edit), [table.columns]);
  const counts = useMemo(() => editCounts(table.rows.flatMap((r) => typed.map((c) => c.edit?.(r)))), [table.rows, typed]);
  const focusColumn = focus ? typed.find((c) => c.id === focus.column) : undefined;
  const focusEdit = focus && focusColumn?.edit?.(focus.row);
  // more than a page of rows: one page at a time; a new sort or filter shows the first (or the followed row's) again
  const count = table.rows.length;
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const followPage = follow != null && follow >= 0 ? Math.floor(follow / PAGE_SIZE) : 0;
  const viewKey = JSON.stringify([table.sort, table.filters, followPage]);
  const [paging, setPaging] = useState({ key: viewKey, page: 0 });
  const page = Math.min(paging.key === viewKey ? paging.page : followPage, pages - 1);
  const start = page * PAGE_SIZE;
  const rows = pages > 1 ? table.rows.slice(start, start + PAGE_SIZE) : table.rows;
  const body = useRef<HTMLTableSectionElement>(null);
  useLayoutEffect(() => {
    if (follow != null && follow >= 0) body.current?.querySelector(`tr[data-row="${follow}"]`)?.scrollIntoView({ block: "nearest" });
  }, [follow]);
  // the columns with a dragged width (by id, so a new width alone draws no row again)
  const sizedIds = Object.keys(table.widths).sort().join("\n");
  const sized = useMemo(() => new Set(sizedIds ? sizedIds.split("\n") : []), [sizedIds]);
  const widths = Object.fromEntries(
    table.columns.flatMap((c, i) => (table.widths[c.id] ? [[widthVar(i), `${table.widths[c.id]}px`]] : [])),
  );

  // more than a page of rows: the pages under the table, and at the right end of the line above it (no row of their
  // own), so either is at hand
  const pager = (side: "top" | "bottom") =>
    pages > 1 && (
      <nav
        className={side === "top" ? "dt-tools-pager" : "dt-pager"}
        aria-label={t(side === "top" ? "dataTable.pagesAbove" : "dataTable.pages")}
      >
        <span className="sm mu">
          {t("dataTable.pageRows", {
            from: formatNumber(start + 1),
            to: formatNumber(Math.min(start + PAGE_SIZE, count)),
            count: formatNumber(count),
          })}
        </span>
        <Pagination
          size={side === "top" ? "xs" : "sm"}
          total={pages}
          value={page + 1}
          onChange={(p) => setPaging({ key: viewKey, page: p - 1 })}
          getControlProps={(control) => ({ "aria-label": t(`dataTable.${control}Page`) })}
        />
      </nav>
    );

  return (
    <div className={cx("dt-box", props.className)}>
      <TableTools table={table} title={props.title ?? props["aria-label"]} about={props.about} pages={pager("top")} />
      {typed.length > 0 && (
        <EditsBar
          counts={counts}
          focused={
            focus && focusColumn && focusEdit
              ? {
                  name: [props.rowName?.(focus.row), columnName(focusColumn)].filter(Boolean).join(" · "),
                  edit: focusEdit,
                }
              : null
          }
          valueText={focusColumn?.editText}
          onPutBack={focus && focusEdit ? focusColumn?.putBack?.(focus.row, focusEdit) : undefined}
          putBackHelp={props.putBackHelp}
        />
      )}
      <div className="dt-scroll">
        <table className={cx("dt", props.tableClassName)} aria-label={props["aria-label"]} style={widths}>
          <Head table={table} />
          <DetailsContext.Provider value={detailsValue}>
            <tbody ref={body}>
              {rows.map((row, j) => {
                const i = start + j;
                const key = rowKey(row);
                const shown = details && opened.has(key) ? details(row) : null;
                return (
                  <Fragment key={key}>
                    {ownOrder && props.before?.(row)}
                    <DataRow
                      row={row}
                      index={i}
                      columns={table.columns}
                      ownOrder={ownOrder}
                      sized={sized}
                      attrs={attrsOf(row, i)}
                      fire={fire}
                      focusCell={focusCell}
                    />
                    {shown && (
                      <DetailRow id={key} width={width} open={!!opened.get(key)} onClosed={closed}>
                        {shown}
                      </DetailRow>
                    )}
                  </Fragment>
                );
              })}
              {table.rows.length === 0 && table.total > 0 && (
                <tr>
                  <td colSpan={width} className="dt-note">
                    {t("dataTable.noMatch")}{" "}
                    <Button size="compact-sm" variant="default" onClick={table.clearFilters}>
                      {t("dataTable.clearFilters")}
                    </Button>
                  </td>
                </tr>
              )}
              {table.total === 0 && props.empty && (
                <tr>
                  <td colSpan={width} className="dt-note">
                    {props.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </DetailsContext.Provider>
          {footed && (
            <tfoot>
              <tr>
                {table.columns.map((c) => (
                  <td key={c.id} className={cx(c.numeric && "n", c.className)}>
                    {c.footer}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {pager("bottom")}
    </div>
  );
}

/** A row's details under it: they slide open when drawn and shut when closed, then go (``onClosed``). */
function DetailRow<K>(props: { id: K; width: number; open: boolean; onClosed: (id: K) => void; children: ReactNode }) {
  const { id, open, onClosed } = props;
  // drawn shut first, so the opening slides; drawn while closed (it was paged away sliding shut): gone at once
  const [expanded, setExpanded] = useState(false);
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current && !open) onClosed(id);
    mounted.current = true;
    setExpanded(open);
  }, [open, id, onClosed]);
  return (
    <tr className="dt-detail">
      <td colSpan={props.width}>
        <Collapse expanded={expanded} onTransitionEnd={() => !open && onClosed(id)}>
          <div className="dt-detail-body">{props.children}</div>
        </Collapse>
      </td>
    </tr>
  );
}

type RowView<T> = {
  row: T;
  index: number;
  columns: Column<T>[];
  /** The rows are in the page's own order (a cell may act on it: a list's up / down buttons). */
  ownOrder: boolean;
  /** The columns with a dragged width: their cells clip what does not fit. */
  sized: ReadonlySet<string>;
  attrs: RowAttributes | undefined;
  fire: (row: unknown, index: number, name: string, event: SyntheticEvent) => void;
  focusCell: (row: unknown, column: string) => void;
};

/** One body row; drawn again only when its row, place, columns or (non-handler) attributes change. */
const DataRow = memo(function DataRow<T>({ row, index, columns, ownOrder, sized, attrs, fire, focusCell }: RowView<T>) {
  // cells may read page state through refs (it reaches the row through its attributes): the compiler must not keep
  // the cells of an earlier drawing
  "use no memo";
  const { t } = useTranslation();
  const own: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(attrs ?? {})) {
    own[name] = typeof value === "function" ? (e: SyntheticEvent) => fire(row, index, name, e) : value;
  }
  return (
    <tr data-row={index} {...own}>
      {columns.map((c, n) => {
        const edit = c.edit?.(row);
        const own = c.cellProps?.(row);
        return (
          <td
            key={c.id}
            className={cx(c.numeric && "n", c.className, c.cellClassName?.(row), editClass(edit), sized.has(c.id) && "dt-sized")}
            title={edit && editNote(edit, t, c.editText)}
            onFocus={c.edit && (() => focusCell(row, c.id))}
            {...own}
            style={sized.has(c.id) ? { maxWidth: `var(${widthVar(n)})`, ...own?.style } : own?.style}
          >
            {c.cell ? c.cell(row, index, { ownOrder }) : c.text ? c.text(row) : cellText(c.value?.(row))}
          </td>
        );
      })}
    </tr>
  );
}, sameRow) as <T>(props: RowView<T>) => ReactNode;

function sameRow<T>(a: RowView<T>, b: RowView<T>): boolean {
  if (a.row !== b.row || a.index !== b.index || a.columns !== b.columns || a.sized !== b.sized || a.fire !== b.fire) return false;
  if (a.ownOrder !== b.ownOrder) return false;
  if (a.focusCell !== b.focusCell) return false;
  const [x, y] = [a.attrs ?? {}, b.attrs ?? {}] as Record<string, unknown>[];
  const keys = Object.keys(x as object);
  if (keys.length !== Object.keys(y as object).length) return false;
  return keys.every((k) => {
    const [p, q] = [x?.[k], y?.[k]];
    if (typeof p === "function") return typeof q === "function";
    if (k === "style" && p && q) return JSON.stringify(p) === JSON.stringify(q);
    return p === q;
  });
}

/** The headings (sort buttons, dragged to move a column), the group headings over them and the filter boxes. */
function Head<T>({ table }: { table: DataTableModel<T> }) {
  // a heading function (select all) may read page state through a ref: drawn anew with the table
  "use no memo";
  const { t } = useTranslation();
  const [drag, setDrag] = useState<{ id: string; over?: string; after?: boolean } | null>(null);
  const several = table.sort.length > 1;
  const drop = (target: Column<T>, after: boolean) => {
    if (!drag || drag.id === target.id) return;
    const ids = table.movable.map((c) => c.id);
    const from = ids.indexOf(drag.id);
    const at = ids.indexOf(target.id) + (after ? 1 : 0);
    table.move(drag.id, from < at ? at - 1 : at);
  };
  return (
    <thead>
      {table.columns.some((c) => c.group) && (
        <tr>
          {groupSpans(table.columns).map((g) => (
            <th key={g.key} colSpan={g.span} className={g.group ? "grp" : undefined}>
              {g.group?.header}
            </th>
          ))}
        </tr>
      )}
      <tr>
        {table.columns.map((c, i) => {
          const sorted = sortedBy(table.sort, c.id);
          const w = `var(${widthVar(i)})`;
          const set = table.widths[c.id];
          const over = drag?.over === c.id && drag.id !== c.id && (drag.after ? "drop-after" : "drop-before");
          const header = typeof c.header === "function" ? c.header(table.rows) : c.header;
          return (
            <th
              key={c.id}
              className={cx(c.numeric && "n", c.className, over, drag?.id === c.id && "dragging")}
              title={c.title}
              style={{ width: w, minWidth: w, maxWidth: w }}
              aria-sort={sorted?.place === 1 ? (sorted.descending ? "descending" : "ascending") : undefined}
              draggable={!c.fixed && table.movable.length > 1}
              onDragStart={(e) => {
                if (document.body.classList.contains("resizing")) {
                  e.preventDefault();
                  return;
                }
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", columnName(c));
                setDrag({ id: c.id });
              }}
              onDragOver={(e) => {
                if (!drag || c.fixed) return;
                e.preventDefault();
                const box = e.currentTarget.getBoundingClientRect();
                const after = e.clientX > box.left + box.width / 2;
                if (drag.over !== c.id || drag.after !== after) setDrag({ ...drag, over: c.id, after });
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (drag?.over === c.id) drop(c, !!drag.after);
                setDrag(null);
              }}
              onDragEnd={() => setDrag(null)}
            >
              {isSortable(c) ? (
                <button
                  type="button"
                  className="sort-head"
                  title={c.title ? `${c.title}\n\n${t("dataTable.sortHelp")}` : t("dataTable.sortHelp")}
                  onClick={(e) => table.sortBy(c.id, e.shiftKey)}
                >
                  {header}
                  <span aria-hidden className={sorted ? "sort-mark on" : "sort-mark"}>
                    {sorted ? (sorted.descending ? SORT_MARK.descending : SORT_MARK.ascending) : SORT_MARK.none}
                  </span>
                  {sorted && several && <sup>{sorted.place}</sup>}
                </button>
              ) : (
                header
              )}
              {/* a drag on a heading's right edge: the width follows the pointer (on the table, no drawing) and is kept on
                  release (ui/resizeHandle) */}
              <span
                className="dt-resize"
                {...resizeHandle({
                  value: set ?? null,
                  min: COLUMN_WIDTH.min,
                  max: COLUMN_WIDTH.max,
                  label: t("dataTable.resize", { column: columnName(c) }),
                  help: t("dataTable.resizeHelp"),
                  current: (handle) => (handle.parentElement as HTMLElement).getBoundingClientRect().width,
                  live: (handle, px) => handle.closest("table")?.style.setProperty(widthVar(i), `${px}px`),
                  commit: (px) => table.setWidth(c.id, px),
                })}
              />
            </th>
          );
        })}
      </tr>
      {table.filterRow && (
        <tr className="dt-filters">
          {table.columns.map((c) => (
            <th key={c.id} className={c.numeric ? "n" : undefined}>
              {isFilterable(c) && (
                <FilterBox
                  value={table.filters[c.id] ?? ""}
                  onChange={(text) => table.setFilter(c.id, text)}
                  numeric={!!c.numeric}
                  name={columnName(c)}
                />
              )}
            </th>
          ))}
        </tr>
      )}
    </thead>
  );
}

/** Consecutive columns of one group under one heading; columns of no group under an empty one each. */
function groupSpans<T>(columns: Column<T>[]): { key: string; span: number; group?: Column<T>["group"] }[] {
  const out: { key: string; span: number; group?: Column<T>["group"] }[] = [];
  for (const c of columns) {
    const last = out[out.length - 1];
    if (c.group && last?.group?.id === c.group.id) last.span++;
    else out.push({ key: c.group ? `${c.group.id}:${c.id}` : c.id, span: 1, group: c.group });
  }
  return out;
}

/** The line above a table: show the filter boxes, choose and order the columns, and what sort, filters and hidden
 *  columns are in force (each with its button to undo it). */
function TableTools<T>({ table, title, about, pages }: { table: DataTableModel<T>; title?: string; about?: string[]; pages?: ReactNode }) {
  const { t } = useTranslation();
  const filterable = table.columns.some(isFilterable);
  const names = new Map(table.columns.map((c) => [c.id, columnName(c)]));
  return (
    <div className="dt-tools">
      {filterable && <FilterRowButton open={table.filterRow} filtered={table.filtered} onToggle={table.showFilterRow} />}
      {table.movable.length > 1 && <ColumnOrder table={table} />}
      {table.rows.length > 0 && (
        <FileButtons
          viewOf={(kind) => dataTableView(table, kind)}
          rows={table.rows.length}
          title={title}
          details={about}
          size="compact-sm"
          excelHelp={t(table.partial ? "dataTable.excelLoadedHelp" : "dataTable.excelHelp", { count: table.rows.length })}
          printHelp={t("dataTable.printHelp", { count: table.rows.length })}
        />
      )}
      <ResetTableButton disabled={!table.customized} onReset={table.resetAll} />
      {table.hidden.length > 0 && (
        <span className="dt-state dt-hidden">
          {t("dataTable.hiddenNote", { names: table.hidden.map(columnName).join(", ") })}
          <Button size="compact-sm" variant="default" onClick={table.showAll}>
            {t("dataTable.showAll")}
          </Button>
        </span>
      )}
      <SortNote
        keys={table.sort.map((k) => ({ name: names.get(k.field) ?? k.field, descending: k.descending }))}
        onClear={table.clearSort}
      />
      {table.filtered > 0 && <ShownNote shown={table.rows.length} total={table.total} onClear={table.clearFilters} />}
      {table.partial && (table.sort.length > 0 || table.filtered > 0) && (
        <span className="sm mu">{t("dataTable.loadedOnly", { count: table.total })}</span>
      )}
      {pages}
    </div>
  );
}

/** The Columns button: the movable columns top to bottom (= left to right), each with its tick box (shown or hidden)
 *  and arrows to move it; every column shown again, and the page's own order back. */
function ColumnOrder<T>({ table }: { table: DataTableModel<T> }) {
  const { t } = useTranslation();
  const last = table.movable.length - 1;
  const hidden = table.hiddenIds;
  const lastShown = table.movable.length - hidden.size === 1;
  return (
    <Popover position="bottom-start" width={300} shadow="md" withArrow trapFocus returnFocus>
      <Popover.Target>
        <Button
          size="compact-sm"
          variant={table.reordered || hidden.size ? "light" : "default"}
          leftSection={<Icon name="columns" size={15} />}
        >
          {hidden.size ? t("dataTable.columnsHidden", { count: hidden.size }) : t("dataTable.columns")}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <div className="stack" style={{ gap: 8 }}>
          <b>{t("dataTable.columnsTitle")}</b>
          <span className="sm mu">{t("dataTable.columnsHelp")}</span>
          <ol className="dt-order">
            {table.movable.map((c, i) => {
              const name = columnName(c);
              const shown = !hidden.has(c.id);
              return (
                <li key={c.id} className={shown ? undefined : "off"}>
                  <Checkbox
                    className="grow"
                    size="xs"
                    label={name}
                    aria-label={t("dataTable.showColumn", { column: name })}
                    checked={shown}
                    // the last column shown stays
                    disabled={shown && lastShown}
                    onChange={(e) => table.setHidden(c.id, !e.currentTarget.checked)}
                  />
                  <ActionIcon
                    variant="default"
                    disabled={i === 0}
                    aria-label={t("dataTable.moveLeft", { column: name })}
                    title={t("dataTable.moveLeft", { column: name })}
                    onClick={() => table.move(c.id, i - 1)}
                  >
                    <Icon name="arrowUp" size={15} />
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    disabled={i === last}
                    aria-label={t("dataTable.moveRight", { column: name })}
                    title={t("dataTable.moveRight", { column: name })}
                    onClick={() => table.move(c.id, i + 1)}
                  >
                    <Icon name="arrowDown" size={15} />
                  </ActionIcon>
                </li>
              );
            })}
          </ol>
          <Button size="compact-sm" variant="default" disabled={!hidden.size} onClick={table.showAll}>
            {t("dataTable.showAll")}
          </Button>
          <Button size="compact-sm" variant="default" disabled={!table.reordered} onClick={table.defaultOrder}>
            {t("dataTable.defaultOrder")}
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
